<template>
  <div class="pa-5">
    <div v-if="showFinancialList">
      <div class="d-flex mb-8">
        <h2>
          {{ $t('financial.label.payments') }}
        </h2>

        <v-menu v-if="canEditPayments" bottom rounded offset-y>
          <template v-slot:activator="{ on }">
            <v-btn
              class="ml-3"
              elevation="0"
              color="primary"
              v-on="on"
              outlined
              small
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-list dense>
              <v-list-item @click="openRecalculateModal()" link>
                <v-icon
                  class="mr-3 grey--text text--lighten-1"
                  style="transform: scale(0.85); opacity: 0.75"
                >
                  mdi-calculator
                </v-icon>
                <span class="body-2">
                  {{ calculatedPaymentLabel }}
                </span>
              </v-list-item>
              <v-list-item
                v-if="lastPayment"
                @click="openChargebackModal()"
                link
              >
                <v-icon
                  class="mr-3 grey--text text--lighten-1"
                  style="transform: scale(0.85); opacity: 0.75"
                >
                  mdi-cash-refund
                </v-icon>
                <span class="body-2">
                  {{ $t('financial.updatePayments.label.createChargeback') }}
                </span>
              </v-list-item>
              <v-list-item @click="openSinglePayment()" link>
                <v-icon
                  class="mr-3 grey--text text--lighten-1"
                  style="transform: scale(0.85); opacity: 0.75"
                >
                  mdi-cash-plus
                </v-icon>
                <span class="body-2">
                  {{ $t('financial.updatePayments.label.createSinglePayment') }}
                </span>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>

      <v-data-table
        disable-sort
        mobile-breakpoint="20"
        :items-per-page="10"
        :headers="paymentHeaders"
        :items="appointment.payments"
        :expanded.sync="expandedPayments"
        item-key="id"
        show-expand
        single-expand
        :footer-props="{
          'items-per-page-text': '',
          'items-per-page-options': [],
        }"
        :no-data-text="$t('appointment.table.noData')"
        :loading-text="$t('appointment.table.loading')"
      >
        <template v-slot:item.created_at ="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template v-slot:item.type ="{ item }">
          {{
            item.type
              ? $t(`financial.payment.label.paymentType.${item.type}`)
              : '---'
          }}
        </template>
        <template v-slot:item.status ="{ item }">
          {{ formatPaymentStatus(item.status) }}
        </template>
        <template v-slot:item.total ="{ item }">
          <div class="text-right">
            {{ formatMoney(item.total) }}
          </div>
        </template>
        <!-- Only calculated payments have a breakdown -->
        <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
          <v-btn
            v-if="item.type === 'CALCULATED'"
            @click="expand(!isExpanded)"
            :title="$t('financial.breakdown.show')"
            icon
            small
          >
            <v-icon small>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <PaymentFinancialDetails :key="item.id" :payment-id="item.id" />
          </td>
        </template>
      </v-data-table>

      <SinglePayment :appointment-id="appointmentId" />
      <CalculatedPayment :appointment-id="appointmentId" />
      <Chargeback :appointment-id="appointmentId" :payment="lastPayment" />
    </div>
    <div v-else>
      <p class="grey--text text-center mb-0">
        {{ $t('financial.emptyState') }}
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { itemStatusHeader } from '@/views/listHeaders/itemStatusHeader'
import { catalogDate, catalogMonetary } from '@/util/catalog'
import { paymentHeader } from '@/views/listHeaders/paymentHeader'
import CalculatedPayment from '@/components/financial/CalculatedPayment'
import Chargeback from '@/components/financial/Chargeback'
import SinglePayment from '@/components/financial/SinglePayment'
import PaymentFinancialDetails from '@/components/financial/PaymentFinancialDetails'
export default {
  name: 'AdminAppointmentFinancialDetails',

  props: {
    appointmentId: {
      required: true,
    },
  },

  components: {
    CalculatedPayment,
    Chargeback,
    SinglePayment,
    PaymentFinancialDetails,
  },

  data() {
    return {
      itemStatusHeaders: itemStatusHeader,
      paymentHeaders: paymentHeader,
      formatDate: catalogDate,
      formatMoney: catalogMonetary,
      expandedPayments: [],
    }
  },

  computed: {
    ...mapGetters(['appointment']),

    lastPayment() {
      return this.appointment.payments.find(
        (payment) => payment.type == 'CALCULATED' && payment.chargeback == null
      )
    },

    calculatedPaymentLabel() {
      if (!this.lastPayment) {
        return this.$t('financial.updatePayments.label.createCalculated')
      }
      return this.$t('financial.updatePayments.label.recalculate')
    },

    canEditPayments() {
      return this.appointment.status === 'DONE' || this.appointment.status === 'CANCELED'
    },

    /**
     * A trip reimbursement is paid while the job may still be open or scheduled with another host,
     * so the list shows up whenever there is a payment; only the edit menu waits for the job to end.
     */
    showFinancialList() {
      return this.canEditPayments || (this.appointment.payments || []).length > 0
    },
  },

  methods: {
    ...mapMutations(['openModal']),

    openRecalculateModal() {
      this.openModal('APPOINTMENT_CALCULATE_PAYMENT_MODAL')
    },

    openSinglePayment() {
      this.openModal('APPOINTMENT_SINGLE_PAYMENT_MODAL')
    },

    openChargebackModal() {
      this.openModal('APPOINTMENT_CHARGEBACK_MODAL')
    },

    formatPaymentStatus(status) {
      if (!status)
        return this.$t('financial.payment.label.status.waitingForGeneration')

      return this.$t(`financial.payment.label.status.${status}`)
    },
  },
}
</script>
